/**
 * 红包雨原因码 → 多语言文案（契约 rer-spec §6）
 *
 * 词条在 redEnvelopeRain.reason.<CODE>；带插值的码另配 <CODE>_PLAIN：参数不齐时用不带插值的版本，
 * 避免 i18next 把 {{required}} 原样显示出来。未登记的码一律走 redEnvelopeRain.reason.UNKNOWN。
 * IP / 设备类、SYSTEM_ERROR 后端本就不下发参数（防被试探阈值），这里也只给模糊文案。
 */

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** 已登记文案的原因码（与后端枚举 RedEnvelopeRainReason 逐字一致） */
export const RED_ENVELOPE_RAIN_REASON_CODES = [
  "BUSY",
  "ACTIVITY_INVALID",
  "TEST_USER",
  "NOT_IN_SLOT",
  "ALREADY_CLAIMED",
  "TERMINAL_NOT_SUPPORT",
  "DAILY_LIMIT_REACHED",
  "LOGIN_IP_USERS_EXCEED",
  "REG_IP_USERS_EXCEED",
  "DEVICE_USERS_EXCEED",
  "DEVICE_ID_MISSING",
  // 2026-10-13 后端新增的 4 项领取条件（只给模糊文案，不显示上限 / 当前次数）
  "NO_WITHDRAW_INFO",
  "LOGIN_IP_CLAIMS_EXCEED",
  "REG_IP_CLAIMS_EXCEED",
  "DEVICE_CLAIMS_EXCEED",
  "FIRST_DEPOSIT_REQUIRED",
  "CONDITION_NOT_MET",
  "TIER_NOT_REACHED",
  "NOTHING_TO_CLAIM",
  "WAVE_EXHAUSTED",
  "WAVE_PARTIAL",
  "SYSTEM_ERROR",
] as const;

const KNOWN_CODES = new Set<string>(RED_ENVELOPE_RAIN_REASON_CODES);

/** 已登记的统计窗口 / 基数 / 投注口径 / 终端（词条 key 后缀），未登记的视为参数不齐 */
const WINDOW_CODES = new Set([
  "HIST",
  "TODAY",
  "WEEK",
  "MONTH",
  "YESTERDAY",
  "LAST_WEEK",
  "LAST_MONTH",
  "LAST_7_DAYS",
]);
const BASE_CODES = new Set(["DEPOSIT", "NET_LOSS", "BET"]);
const BASIS_CODES = new Set(["VALID_BET", "TOTAL_BET"]);
const TERMINAL_CODES = new Set(["APP", "PWA", "BROWSER"]);

/** 结构化文案：text = 主句；lines = 明细行（CONDITION_NOT_MET 逐行列出，行间连接词已插好） */
export interface RainReasonView {
  code: string;
  text: string;
  lines: string[];
}

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;

/** 终端码列表 → 「App, PWA」；全部未知 / 为空返回 null */
export function formatRainTerminals(t: Translate, raw: unknown): string | null {
  if (!Array.isArray(raw)) return null;
  const names = raw
    .map(v => String(v ?? "").toUpperCase())
    .filter(code => TERMINAL_CODES.has(code))
    .map(code => t(`redEnvelopeRain.terminal.${code}`));
  return names.length > 0 ? names.join(", ") : null;
}

export interface RainReasonOptions {
  /** 金额格式化（货币符号 + 本地化） */
  formatAmount: (n: number) => string;
  /** TERMINAL_NOT_SUPPORT 参数缺失时的兜底（status.allowedTerminals） */
  fallbackAllowedTerminals?: unknown;
}

/** 投注口径词：basis.<VALID_BET|TOTAL_BET>；未知返回 null */
const basisWord = (t: Translate, basis: unknown): string | null => {
  const b = String(basis ?? "").toUpperCase();
  return BASIS_CODES.has(b) ? t(`redEnvelopeRain.basis.${b}`) : null;
};

const windowWord = (t: Translate, win: unknown): string | null => {
  const w = String(win ?? "").toUpperCase();
  return WINDOW_CODES.has(w) ? t(`redEnvelopeRain.window.${w}`) : null;
};

/** CONDITION_NOT_MET 明细行（参数齐全才出行；任一行缺参数则整体不出明细，只显示通用句） */
function buildConditionLines(
  t: Translate,
  params: Record<string, unknown> | null,
  formatAmount: (n: number) => string
): string[] {
  if (!params) return [];
  const win = windowWord(t, params.window);
  if (!win) return [];
  const lines: string[] = [];
  const deposit = asRecord(params.deposit);
  if (deposit) {
    const required = toNumber(deposit.required);
    const actual = toNumber(deposit.actual);
    if (required === null || actual === null) return [];
    lines.push(
      t("redEnvelopeRain.reason.depositLine", {
        window: win,
        required: formatAmount(required),
        actual: formatAmount(actual),
      })
    );
  }
  const bet = asRecord(params.bet);
  if (bet) {
    const required = toNumber(bet.required);
    const actual = toNumber(bet.actual);
    const basis = basisWord(t, bet.basis);
    if (required === null || actual === null || !basis) return [];
    lines.push(
      t("redEnvelopeRain.reason.betLine", {
        window: win,
        basis,
        required: formatAmount(required),
        actual: formatAmount(actual),
      })
    );
  }
  if (lines.length === 2) {
    // ANY = 满足其一（或），ALL = 同时满足（且）；mode 缺失 / 未知时不插连接词，避免误导
    const mode = String(params.mode ?? "").toUpperCase();
    const joiner =
      mode === "ANY"
        ? t("redEnvelopeRain.reason.joinAny")
        : mode === "ALL"
          ? t("redEnvelopeRain.reason.joinAll")
          : "";
    return joiner ? [lines[0], joiner, lines[1]] : lines;
  }
  return lines;
}

/**
 * 原因码 → 结构化文案。
 * 未知码 / 空码 → UNKNOWN；带参码参数不齐 → <CODE>_PLAIN。
 */
export function getRainReasonView(
  t: Translate,
  code: string | null | undefined,
  params: Record<string, unknown> | null | undefined,
  options: RainReasonOptions
): RainReasonView {
  const c = String(code ?? "");
  if (!c || !KNOWN_CODES.has(c)) {
    return {
      code: c || "UNKNOWN",
      text: t("redEnvelopeRain.reason.UNKNOWN"),
      lines: [],
    };
  }
  const p = asRecord(params);
  const plain = () => ({
    code: c,
    text: t(`redEnvelopeRain.reason.${c}_PLAIN`),
    lines: [],
  });

  switch (c) {
    case "TERMINAL_NOT_SUPPORT": {
      const terminals =
        formatRainTerminals(t, p?.allowed) ??
        formatRainTerminals(t, options.fallbackAllowedTerminals);
      if (!terminals) return plain();
      return {
        code: c,
        text: t("redEnvelopeRain.reason.TERMINAL_NOT_SUPPORT", { terminals }),
        lines: [],
      };
    }
    case "TIER_NOT_REACHED": {
      if (!p) return plain();
      const win = windowWord(t, p.window);
      const baseCode = String(p.base ?? "").toUpperCase();
      // base = BET 时用投注口径词（有效投注 / 投注）代替 base.BET
      const base =
        baseCode === "BET"
          ? basisWord(t, p.basis)
          : BASE_CODES.has(baseCode)
            ? t(`redEnvelopeRain.base.${baseCode}`)
            : null;
      const required = toNumber(p.required);
      const actual = toNumber(p.actual);
      if (!win || !base || required === null || actual === null) {
        return plain();
      }
      return {
        code: c,
        text: t("redEnvelopeRain.reason.TIER_NOT_REACHED", {
          window: win,
          base,
          required: options.formatAmount(required),
          actual: options.formatAmount(actual),
        }),
        lines: [],
      };
    }
    case "WAVE_PARTIAL": {
      const paid = toNumber(p?.paid);
      if (paid === null) return plain();
      return {
        code: c,
        text: t("redEnvelopeRain.reason.WAVE_PARTIAL", {
          paid: options.formatAmount(paid),
        }),
        lines: [],
      };
    }
    case "CONDITION_NOT_MET":
      return {
        code: c,
        text: t("redEnvelopeRain.reason.CONDITION_NOT_MET"),
        lines: buildConditionLines(t, p, options.formatAmount),
      };
    default:
      return { code: c, text: t(`redEnvelopeRain.reason.${c}`), lines: [] };
  }
}

/** toast 用的单行文案（明细行用空格拼接） */
export function getRainReasonText(
  t: Translate,
  code: string | null | undefined,
  params: Record<string, unknown> | null | undefined,
  options: RainReasonOptions
): string {
  const view = getRainReasonView(t, code, params, options);
  return view.lines.length > 0
    ? `${view.text} ${view.lines.join(" ")}`
    : view.text;
}
